/**
 * TimelinePanel - Visual timeline for scene editing
 * 
 * Spec 019 owns this panel: Items (sprites, dialogs), Audio (sounds) and
 * Events lanes; six keyframe rows per sprite (dialog and sound tracks show
 * their time bars only); zoom, ruler scrubbing, keyframe selection, drag,
 * popover, context menu and the keyframe clipboard. Keyframe write rules
 * (value, tag, orientation fork) are the store's (spec 006). The State row
 * also shows the line-expression keys that play in the active orientation
 * (006's precedence rule) as read-only markers (`data-derived="expression"`):
 * they cannot be selected, moved, copied or deleted (spec 019 D-3).
 *
 * Spec 040: a background with two or more states gets a "Background" row
 * first in the Items group, with one `state` row on the reserved track
 * (`BACKGROUND_TRACK_ID`). It is not a sprite: no selection as one, no
 * delete, rename or reorder. `+` adds a change to the state after the one
 * playing (D-3); a diamond's popover edits the state and the change's
 * transition through `TransitionFields` (D-4), and shows the transition's
 * type as its tooltip and a bar spanning its duration.
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useSceneStore, TIMELINE_ROW_PROPERTIES, keyframesToArray, type Keyframe } from '../../../stores/sceneStore';
import type { Orientation } from '../../../../../Shared/src/@types';
import { timelineLengthMs, previewDurationMs } from '../../../utils/sceneDuration';
import { inertWhen } from '../../../utils/inert';
import { computeDialogTimeline } from '../../../../../Shared/src/utils/sceneTiming';
import { candidateKeyframes } from '../../../../../Shared/src/utils/interpolate';
import { isFlipProperty } from '../../../../../Shared/src/utils/keyframeWrite';
import { resolveLineExpressionKeys } from '../../../../../Shared/src/utils/playbackKeyframes';
import { BACKGROUND_TRACK_ID, backgroundStates } from '../../../../../Shared/src/utils/backgroundStates';
import { backgroundTransitionText } from '../../../../../Shared/src/operations/backgroundOperations';
import type { Transition } from '../../../../../Shared/src/@types';
import TransitionFields from '../properties/TransitionFields';
import PlayFromStartIcon from '../../preview/PlayFromStartIcon';
import importedStyles from './TimelinePanel.module.scss';

const styles = importedStyles || {} as Record<string, string>;

// =============================================================================
// TYPES
// =============================================================================

interface TrackItem {
  id: string;
  name: string;
  type: 'sprite' | 'dialog' | 'sound' | 'signal' | 'background';
  icon: string;
  startTime: number;
  endTime?: number;
  /** A short warning shown after the name (a sound without media). */
  note?: string;
  keyframes?: Keyframe[];
  expandable?: boolean;
  properties?: string[];
}

interface LaneGroup {
  id: string;
  name: string;
  icon: string;
  collapsed: boolean;
  tracks: TrackItem[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

const SPRITE_PROPERTIES = [
  { key: 'position', label: 'Position' },
  { key: 'hinge', label: 'Hinge' },
  { key: 'transform.scale', label: 'Scale' },
  { key: 'transform.rotation', label: 'Rotation' },
  { key: 'transform.flipX', label: 'Flip X' },
  { key: 'transform.flipY', label: 'Flip Y' },
  { key: 'opacity', label: 'Opacity' },
  { key: 'state', label: 'State' },
];

/** The Background row's one property row (spec 040 D-3). */
const BACKGROUND_PROPERTIES = [{ key: 'state', label: 'State' }];

/** The rows a track shows: a sprite's eight, the Background's one, none for the rest. */
const propertiesOf = (track: Pick<TrackItem, 'type'>) =>
  track.type === 'sprite' ? SPRITE_PROPERTIES : track.type === 'background' ? BACKGROUND_PROPERTIES : [];

/** What a key with no transition is edited as: a cut, 500 ms when it becomes one. */
const CUT: Transition = { type: 'none', duration: 500 };

// Dialog and sound tracks have no keyframe rows until spec 007 gives them
// automation: nothing interpolates dialog or sound properties (F-021).

const PIXELS_PER_SECOND = 100;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
/** A keyframe drag shorter than this (px) is a click. */
const DRAG_THRESHOLD_PX = 2;
/** A marquee this size (px) or larger selects. */
const MARQUEE_MIN_PX = 5;

type KeyframeRef = { trackId: string; keyframeId: string };

// Compound properties that should be grouped into a single keyframe diamond
const COMPOUND_PROPERTIES: Record<string, string[]> = {
  'position': ['position.x', 'position.y'],
  'hinge': ['hinge.x', 'hinge.y'],
};

// Sub-property display labels
const SUB_PROPERTY_LABELS: Record<string, string> = {
  'position.x': 'X',
  'position.y': 'Y',
  'hinge.x': 'X',
  'hinge.y': 'Y',
};

interface KeyframeGroup {
  time: number;
  keyframes: Keyframe[];
}

/** Group keyframes by time so compound properties (e.g. position.x + position.y) render as one diamond */
function groupKeyframesByTime(keyframes: Keyframe[]): KeyframeGroup[] {
  const groups = new Map<number, Keyframe[]>();
  for (const kf of keyframes) {
    const existing = groups.get(kf.time);
    if (existing) {
      existing.push(kf);
    } else {
      groups.set(kf.time, [kf]);
    }
  }
  return Array.from(groups.entries()).map(([time, kfs]) => ({ time, keyframes: kfs }));
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface TimeRulerProps {
  duration: number;
  zoom: number;
  scrollLeft: number;
  width: number;
}

const TimeRuler: React.FC<TimeRulerProps> = ({ duration, zoom, scrollLeft, width }) => {
  const pixelsPerSecond = PIXELS_PER_SECOND * zoom;
  const startSecond = Math.floor(scrollLeft / pixelsPerSecond);
  const endSecond = Math.ceil((scrollLeft + width) / pixelsPerSecond);
  
  const markers: JSX.Element[] = [];
  
  for (let s = startSecond; s <= endSecond && s <= duration / 1000; s++) {
    const x = s * pixelsPerSecond;
    markers.push(
      <div
        key={s}
        className={styles.timeMarker}
        style={{ left: x }}
        data-second={s}
      >
        <span className={styles.timeLabel}>{s}s</span>
        <div className={styles.timeTick} />
      </div>
    );
    
    // Sub-markers (every 100ms at high zoom)
    if (zoom >= 1) {
      for (let sub = 1; sub < 10; sub++) {
        const subX = (s + sub / 10) * pixelsPerSecond;
        if (subX >= scrollLeft && subX <= scrollLeft + width) {
          markers.push(
            <div
              key={`${s}-${sub}`}
              className={styles.subTick}
              style={{ left: subX }}
            />
          );
        }
      }
    }
  }
  
  return <div className={styles.timeRuler}>{markers}</div>;
};

// =============================================================================
// LANE GROUP HEADER
// =============================================================================

interface LaneGroupHeaderProps {
  group: LaneGroup;
  onToggle: () => void;
  onAddTrack?: () => void;
}

// The Events group has no add button: signals are not authored (spec 007)
const ADD_TRACK_TITLES: Record<string, string> = { items: 'Add Item', sounds: 'Add Sound' };

const LaneGroupHeader: React.FC<LaneGroupHeaderProps> = ({ group, onToggle, onAddTrack }) => (
  <div className={styles.laneGroupHeader}>
    <button 
      className={`${styles.groupToggle} ${group.collapsed ? '' : styles.expanded}`}
      onClick={onToggle}
    >
      ▶
    </button>
    <span className={styles.groupIcon}>{group.icon}</span>
    <span className={styles.groupName}>{group.name}</span>
    <span className={styles.groupCount}>{group.tracks.length}</span>
    {onAddTrack && (
      <button className={styles.addTrackButton} onClick={onAddTrack} title={ADD_TRACK_TITLES[group.id] ?? 'Add'}>
        +
      </button>
    )}
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const TimelinePanel: React.FC = () => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const tracksAreaRef = useRef<HTMLDivElement>(null);
  const rulerAreaRef = useRef<HTMLDivElement>(null);
  const labelsColumnRef = useRef<HTMLDivElement>(null);
  const tracksColumnRef = useRef<HTMLDivElement>(null);
  const [timelineWidth, setTimelineWidth] = useState(800);
  
  // Context menu (on a diamond: its targets; on a row: Paste here) and keyframe editor
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; trackId: string; property: string; targets: KeyframeRef[] } | null>(null);
  const [keyframeEditor, setKeyframeEditor] = useState<{ x: number; y: number; trackId: string; keyframes: Keyframe[]; property: string } | null>(null);
  
  // Dragging state
  // A keyframe drag previews locally and commits once on release (spec 019)
  const [draggingKeyframe, setDraggingKeyframe] = useState<{ items: Array<KeyframeRef & { originalTime: number }> } | null>(null);
  const [dragDeltaMs, setDragDeltaMs] = useState(0);
  const suppressNextClick = useRef(false);
  const [draggingDialog, setDraggingDialog] = useState<{ dialogId: string } | null>(null);
  // A sound bar drag (spec 007): `move` shifts start (and end if set), `end` sets the end
  const [draggingSound, setDraggingSound] = useState<{ soundId: string; mode: 'move' | 'end'; startTime: number; endTime?: number; barEnd: number } | null>(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const dragStartX = useRef(0);
  const dragStartTime = useRef(0);
  
  // Marquee selection state
  const [marquee, setMarquee] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);
  
  // Store state
  const scene = useSceneStore((state) => state.scene);
  const selection = useSceneStore((state) => state.selection);
  const isPlaying = useSceneStore((state) => state.isPlaying);
  const playheadPosition = useSceneStore((state) => state.playheadPosition);
  const previewOrientation = useSceneStore((state) => state.previewOrientation);
  const storeKeyframes = useSceneStore((state) => state.keyframes);
  const timelineZoom = useSceneStore((state) => state.timelineZoom);
  
  // Current orientation for responsive position resolution
  const orientation: Orientation = previewOrientation;
  
  // Panel state from store (persisted)
  const expandedTracks = useSceneStore((state) => state.expandedTracks);
  const collapsedGroups = useSceneStore((state) => state.collapsedGroups);
  const selectedKeyframe = useSceneStore((state) => state.selectedKeyframe);
  const timelineScrollLeft = useSceneStore((state) => state.timelineScrollLeft);
  const timelineScrollTop = useSceneStore((state) => state.timelineScrollTop);
  
  // Store actions
  const play = useSceneStore((state) => state.play);
  const playFromStart = useSceneStore((state) => state.playFromStart);
  const jumpPreview = useSceneStore((state) => state.jumpPreview);
  const pause = useSceneStore((state) => state.pause);
  const stop = useSceneStore((state) => state.stop);
  const setPlayhead = useSceneStore((state) => state.setPlayhead);
  const selectSingle = useSceneStore((state) => state.selectSingle);
  const addSprite = useSceneStore((state) => state.addSprite);
  const addDialog = useSceneStore((state) => state.addDialog);
  const updateDialog = useSceneStore((state) => state.updateDialog);
  const addSound = useSceneStore((state) => state.addSound);
  const setSoundTiming = useSceneStore((state) => state.setSoundTiming);
  const addKeyframeAtPlayhead = useSceneStore((state) => state.addKeyframeAtPlayhead);
  const updateKeyframeInStore = useSceneStore((state) => state.updateKeyframe);
  const moveKeyframes = useSceneStore((state) => state.moveKeyframes);
  const deleteKeyframes = useSceneStore((state) => state.deleteKeyframes);
  const copyKeyframes = useSceneStore((state) => state.copyKeyframes);
  const pasteKeyframes = useSceneStore((state) => state.pasteKeyframes);
  const keyframeClipboard = useSceneStore((state) => state.keyframeClipboard);
  const zoomTimeline = useSceneStore((state) => state.zoomTimeline);
  const toggleTrackExpanded = useSceneStore((state) => state.toggleTrackExpanded);
  const toggleGroupCollapsed = useSceneStore((state) => state.toggleGroupCollapsed);
  const setKeyframeSelection = useSceneStore((state) => state.setKeyframeSelection);
  const clearKeyframeSelection = useSceneStore((state) => state.clearKeyframeSelection);
  const selectedKeyframes = useSceneStore((state) => state.selectedKeyframes);
  const setTimelineScroll = useSceneStore((state) => state.setTimelineScroll);
  
  // Measure timeline width to ensure it fills the view
  useEffect(() => {
    if (!timelineRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTimelineWidth(entry.contentRect.width);
      }
    });
    
    observer.observe(timelineRef.current);
    return () => observer.disconnect();
  }, []);
  
  // Calculate scene duration - uses same function as the runtime
  // Ensures timeline extends to fit all content and fills the viewport
  // Always extends a bit past the content so there's room to work
  const duration = useMemo(() => {
    const viewportDuration = (timelineWidth / (PIXELS_PER_SECOND * timelineZoom)) * 1000;
    return timelineLengthMs(scene, viewportDuration, previewDurationMs(scene, storeKeyframes));
  }, [scene, storeKeyframes, timelineWidth, timelineZoom]);
  
  // Build lane groups from scene data
  const laneGroups = useMemo((): LaneGroup[] => {
    if (!scene) return [];
    
    // Items group (the Background row when it can change, sprites, dialogs)
    const backgroundTrack: TrackItem[] = backgroundStates(scene.background).length >= 2
      ? [{ id: BACKGROUND_TRACK_ID, name: 'Background', type: 'background', icon: '🏞️', startTime: 0, endTime: duration, expandable: false }]
      : [];
    const itemTracks: TrackItem[] = [
      ...backgroundTrack,
      ...scene.sprites.map((sprite): TrackItem => ({
        id: sprite.id,
        name: sprite.name,
        type: 'sprite',
        icon: '🖼️',
        startTime: 0,
        endTime: duration,
        expandable: true,
        properties: SPRITE_PROPERTIES.map(p => p.key),
      })),
      ...scene.dialogs.map((dialog): TrackItem => {
        // The shared timing model (spec 007): panel fades + lines
        const timing = computeDialogTimeline(dialog);
        return {
          id: dialog.id,
          name: dialog.name || 'Dialog',
          type: 'dialog',
          icon: '💬',
          startTime: timing.startMs,
          endTime: timing.endMs,
          expandable: false,
        };
      }),
    ];
    
    // Audio group (id `sounds`): each bar spans the sound's play window,
    // to the scene end when it has no End (spec 007)
    const sceneEndMs = previewDurationMs(scene);
    const soundTracks: TrackItem[] = scene.sounds.map((sound): TrackItem => ({
      id: sound.id,
      name: sound.name || 'Sound',
      note: sound.mediaId > 0 ? undefined : 'No audio file',
      type: 'sound',
      icon: sound.type === 'bgm' ? '🎵' : sound.type === 'voice' ? '🎤' : '🔊',
      startTime: sound.startTime || 0,
      endTime: sound.endTime ?? sceneEndMs,
      expandable: false,
    }));
    
    // Events/Signals group
    const signalTracks: TrackItem[] = scene.signals.map((signal): TrackItem => ({
      id: signal.id,
      name: `${signal.type}`,
      type: 'signal',
      icon: '⚡',
      startTime: signal.trigger?.time || 0,
      endTime: signal.trigger?.time || 0, // a marker, not a span
      expandable: false,
    }));
    
    return [
      {
        id: 'items',
        name: 'Items',
        icon: '📦',
        collapsed: collapsedGroups.has('items'),
        tracks: itemTracks,
      },
      {
        id: 'sounds',
        name: 'Audio',
        icon: '🔈',
        collapsed: collapsedGroups.has('sounds'),
        tracks: soundTracks,
      },
      {
        id: 'events',
        name: 'Events',
        icon: '⚡',
        collapsed: collapsedGroups.has('events'),
        tracks: signalTracks,
      },
    ];
  }, [scene, duration, collapsedGroups]);
  
  // Handle scroll - sync both columns vertically and persist scroll position
  const handleTracksScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    setTimelineScroll(target.scrollLeft, target.scrollTop);
    // Sync vertical scroll to labels column
    if (labelsColumnRef.current) {
      labelsColumnRef.current.scrollTop = target.scrollTop;
    }
  }, [setTimelineScroll]);
  
  const handleLabelsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    // Sync vertical scroll to tracks column
    if (timelineRef.current) {
      timelineRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }, []);
  
  // Restore scroll position when component mounts or scroll state changes
  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = timelineScrollLeft;
      timelineRef.current.scrollTop = timelineScrollTop;
    }
    if (labelsColumnRef.current) {
      labelsColumnRef.current.scrollTop = timelineScrollTop;
    }
  }, []);  // Only run on mount
  
  // Update timeline width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (timelineRef.current) {
        setTimelineWidth(timelineRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  // Toggle track expansion - use store
  const toggleExpand = useCallback((trackId: string) => {
    toggleTrackExpanded(trackId);
  }, [toggleTrackExpanded]);
  
  // Toggle group collapse - use store
  const toggleGroup = useCallback((groupId: string) => {
    toggleGroupCollapsed(groupId);
  }, [toggleGroupCollapsed]);
  
  // Handle timeline click to set playhead
  const handleTimelineClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only handle clicks on the timeline area, not on track elements
    if ((e.target as HTMLElement).closest(`.${styles.itemBar}`)) return;
    if ((e.target as HTMLElement).closest(`.${styles.keyframe}`)) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pixelsPerSecond = PIXELS_PER_SECOND * timelineZoom;
    const time = Math.max(0, (x / pixelsPerSecond) * 1000);
    setPlayhead(Math.min(time, duration));
  }, [timelineZoom, setPlayhead, duration]);
  
  // Ruler: a click sets the playhead, a drag scrubs it (clamped to [0, length])
  const handleRulerMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest(`.${styles.playheadHead}`)) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const pixelsPerSecond = PIXELS_PER_SECOND * timelineZoom;
    const time = Math.min(duration, Math.max(0, ((e.clientX - rect.left) / pixelsPerSecond) * 1000));
    setPlayhead(time);
    setIsDraggingPlayhead(true);
    dragStartX.current = e.clientX;
    dragStartTime.current = time;
  }, [timelineZoom, setPlayhead, duration]);

  // Ctrl/Cmd + wheel over the tracks zooms around the time under the pointer
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.deltaY === 0) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const pointerX = e.clientX - rect.left;
      const before = useSceneStore.getState().timelineZoom;
      const timeUnderPointer = (el.scrollLeft + pointerX) / (PIXELS_PER_SECOND * before);
      useSceneStore.getState().zoomTimeline(e.deltaY < 0 ? 'in' : 'out');
      const after = useSceneStore.getState().timelineZoom;
      if (after === before) return;
      requestAnimationFrame(() => {
        el.scrollLeft = Math.max(0, timeUnderPointer * PIXELS_PER_SECOND * after - pointerX);
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [scene !== null]);
  
  // Playhead drag handlers
  const handlePlayheadMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPlayhead(true);
    dragStartX.current = e.clientX;
    dragStartTime.current = playheadPosition;
  }, [playheadPosition]);
  
  // Handle playhead dragging
  useEffect(() => {
    if (!isDraggingPlayhead) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX.current;
      const pixelsPerSecond = PIXELS_PER_SECOND * timelineZoom;
      const deltaTime = (deltaX / pixelsPerSecond) * 1000;
      const newTime = Math.max(0, Math.min(duration, dragStartTime.current + deltaTime));
      setPlayhead(newTime);
    };
    
    const handleMouseUp = () => {
      setIsDraggingPlayhead(false);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPlayhead, timelineZoom, duration, setPlayhead]);
  
  // ==========================================================================
  // JUMPING WHILE ▶ PREVIEW PLAYS (spec 009)
  // ==========================================================================
  // The ruler and the tracks stay live while previewing; every press there is
  // a jump, never an edit: a click plays on from the clicked time, a drag
  // shows the frame under the pointer paused and plays on from the release.
  // Bars, diamonds and the playhead handle select or move nothing.
  const previewTimeAt = useCallback((clientX: number): number => {
    const ruler = rulerAreaRef.current;
    if (!ruler) return 0;
    const rect = ruler.getBoundingClientRect();
    const pps = PIXELS_PER_SECOND * timelineZoom;
    return Math.min(duration, Math.max(0, ((clientX - rect.left) / pps) * 1000));
  }, [timelineZoom, duration]);

  const handlePreviewJumpDown = useCallback((e: React.MouseEvent) => {
    if (!useSceneStore.getState().isPlaying) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.button !== 0) return;
    const startX = e.clientX;
    let dragging = false;
    const onMove = (ev: MouseEvent) => {
      if (!dragging && Math.abs(ev.clientX - startX) < 3) return;
      dragging = true;
      jumpPreview(previewTimeAt(ev.clientX), { hold: true });
    };
    const onUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      jumpPreview(previewTimeAt(dragging ? ev.clientX : startX));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [jumpPreview, previewTimeAt]);

  /** While previewing, clicks under the jump layer reach no bar, diamond or row. */
  const swallowWhilePreviewing = useCallback((e: React.SyntheticEvent) => {
    if (!useSceneStore.getState().isPlaying) return;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Calculate playhead position (absolute — parent scroll container handles offset)
  const playheadX = useMemo(() => {
    const pixelsPerSecond = PIXELS_PER_SECOND * timelineZoom;
    return (playheadPosition / 1000) * pixelsPerSecond;
  }, [playheadPosition, timelineZoom]);
  
  // Add track handlers
  const handleAddItem = useCallback(() => {
    addSprite();
  }, [addSprite]);
  
  const handleAddSound = useCallback(() => {
    addSound({ type: 'sfx', mediaId: 0 });
  }, [addSound]);
  
  


  // ==========================================================================
  // KEYFRAME MANAGEMENT (using store)
  // ==========================================================================
  
  const pixelsPerSecond = PIXELS_PER_SECOND * timelineZoom;
  
  // Keyframes of a track/property that count in the active orientation (the
  // interpolator's own candidate rule, per sub-property for compound rows).
  const getTrackKeyframes = useCallback((trackId: string, property?: string): Keyframe[] => {
    const trackKeyframes = storeKeyframes.get(trackId) || [];
    if (!property) return trackKeyframes;
    const props = COMPOUND_PROPERTIES[property] || [property];
    return props.flatMap((prop) => candidateKeyframes(trackKeyframes, prop, orientation));
  }, [storeKeyframes, orientation]);

  // Line-expression keys that play in the active orientation, by sprite id
  // (spec 006's precedence rule drops those an authored state key overrides).
  const derivedStateKeys = useMemo(() => {
    if (!scene) return new Map<string, Keyframe[]>();
    return resolveLineExpressionKeys(
      { sprites: scene.sprites, dialogs: scene.dialogs, keyframes: keyframesToArray(storeKeyframes) },
      orientation,
    );
  }, [scene, storeKeyframes, orientation]);
  
  // Add a keyframe at the playhead with the store's write rules (spec 006:
  // value at the playhead, orientation tag, fork). A compound row adds all
  // its sub-properties as one undo step.
  const addKeyframe = useCallback((trackId: string, property: string) => {
    const props = COMPOUND_PROPERTIES[property] || [property];
    const { beginHistoryGroup, endHistoryGroup } = useSceneStore.getState();
    beginHistoryGroup();
    try {
      for (const prop of props) addKeyframeAtPlayhead(trackId, prop);
    } finally {
      endHistoryGroup();
    }
  }, [addKeyframeAtPlayhead]);
  
  const selectionHas = useCallback((ids: string[]) =>
    ids.length > 0 && ids.every((id) => selectedKeyframes.some((sk) => sk.keyframeId === id)), [selectedKeyframes]);
  
  // Handle double-click on property row to add keyframe
  const handlePropertyDoubleClick = useCallback((e: React.MouseEvent, trackId: string, property: string) => {
    e.stopPropagation();
    addKeyframe(trackId, property);
  }, [addKeyframe]);
  
  // Right-click on a diamond: Delete / Copy act on the selection when the
  // diamond is part of it, else on that diamond (which becomes the selection).
  const handleKeyframeContextMenu = useCallback((e: React.MouseEvent, trackId: string, property: string, keyframeIds: string[]) => {
    e.preventDefault();
    e.stopPropagation();
    let targets: KeyframeRef[];
    if (selectionHas(keyframeIds)) {
      targets = selectedKeyframes;
    } else {
      targets = keyframeIds.map((keyframeId) => ({ trackId, keyframeId }));
      setKeyframeSelection(targets);
    }
    setContextMenu({ x: e.clientX, y: e.clientY, trackId, property, targets });
    setKeyframeEditor(null);
  }, [selectionHas, selectedKeyframes, setKeyframeSelection]);

  // Right-click on a property row: Paste here
  const handleRowContextMenu = useCallback((e: React.MouseEvent, trackId: string, property: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, trackId, property, targets: [] });
    setKeyframeEditor(null);
  }, []);
  
  // Click on a diamond: select it (all sub-keyframes of a compound) and open
  // the popover; Shift/Cmd/Ctrl-click toggles it in the selection.
  const handleKeyframeClick = useCallback((e: React.MouseEvent, trackId: string, keyframes: Keyframe[], property: string) => {
    e.stopPropagation();
    if (suppressNextClick.current) {
      suppressNextClick.current = false;
      return;
    }
    const kfIds = keyframes.map(kf => kf.id);
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      const without = selectedKeyframes.filter((sk) => !kfIds.includes(sk.keyframeId));
      setKeyframeSelection(selectionHas(kfIds)
        ? without
        : [...without, ...kfIds.map((keyframeId) => ({ trackId, keyframeId }))]);
      return;
    }
    setKeyframeSelection(kfIds.map((keyframeId) => ({ trackId, keyframeId })));
    setContextMenu(null);
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setKeyframeEditor({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      trackId,
      keyframes,
      property,
    });
  }, [selectedKeyframes, selectionHas, setKeyframeSelection]);
  
  // Handle keyframe value change
  const handleKeyframeValueChange = useCallback((trackId: string, keyframeId: string, newValue: number | string, isStringValue = false) => {
    // Keep string values for state property, convert to number for others
    const finalValue = isStringValue ? newValue : (typeof newValue === 'string' ? parseFloat(newValue) || 0 : newValue);
    // An untagged keyframe edited in portrait/landscape forks first (spec 006)
    const editedId = updateKeyframeInStore(trackId, keyframeId, { value: finalValue }, orientation) ?? keyframeId;
    
    // Update local popover state
    if (keyframeEditor) {
      setKeyframeEditor({
        ...keyframeEditor,
        keyframes: keyframeEditor.keyframes.map(kf =>
          kf.id === keyframeId ? { ...kf, id: editedId, value: finalValue, orientation: editedId === keyframeId ? kf.orientation : orientation } : kf
        ),
      });
    }
  }, [updateKeyframeInStore, keyframeEditor, orientation]);
  
  // A background change's transition (spec 040 D-4): the fields changed over the key's transition (a cut when none).
  const handleTransitionChange = useCallback((trackId: string, kf: Keyframe, updates: Partial<Transition>) => {
    const next: Transition = { ...(kf.transition ?? CUT), ...updates };
    updateKeyframeInStore(trackId, kf.id, { transition: next });
    if (keyframeEditor) {
      setKeyframeEditor({
        ...keyframeEditor,
        keyframes: keyframeEditor.keyframes.map((k) => (k.id === kf.id ? { ...k, transition: next } : k)),
      });
    }
  }, [updateKeyframeInStore, keyframeEditor]);

  // Handle easing change (applies to all keyframes in a group)
  const handleEasingChange = useCallback((trackId: string, keyframeIds: string[], easing: Keyframe['easing']) => {
    const idMap = new Map<string, string>();
    const { beginHistoryGroup, endHistoryGroup } = useSceneStore.getState();
    beginHistoryGroup();
    for (const kfId of keyframeIds) {
      idMap.set(kfId, updateKeyframeInStore(trackId, kfId, { easing }, orientation) ?? kfId);
    }
    endHistoryGroup();
    
    if (keyframeEditor) {
      setKeyframeEditor({
        ...keyframeEditor,
        keyframes: keyframeEditor.keyframes.map(kf =>
          keyframeIds.includes(kf.id) ? { ...kf, id: idMap.get(kf.id) ?? kf.id, easing } : kf
        ),
      });
    }
  }, [updateKeyframeInStore, keyframeEditor, orientation]);
  
  // Close keyframe editor when clicking elsewhere or pressing ESC
  useEffect(() => {
    if (!keyframeEditor) return;
    
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.keyframeEditor}`)) {
        setKeyframeEditor(null);
      }
    };
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setKeyframeEditor(null);
      }
    };
    
    // Delay click listener to prevent immediate close
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 100);
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [keyframeEditor]);
  
  // ==========================================================================
  // KEYFRAME DRAGGING
  // ==========================================================================
  
  const handleKeyframeDragStart = useCallback((e: React.MouseEvent, trackId: string, keyframeIds: string[]) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    // Modifier clicks toggle the selection (click handler); no drag
    if (e.shiftKey || e.metaKey || e.ctrlKey) return;
    
    // Dragging a selected diamond moves the whole selection; an unselected
    // one becomes the selection first.
    let refs: KeyframeRef[] = selectedKeyframes;
    if (!selectionHas(keyframeIds)) {
      refs = keyframeIds.map((keyframeId) => ({ trackId, keyframeId }));
      setKeyframeSelection(refs);
    }
    const items: Array<KeyframeRef & { originalTime: number }> = [];
    for (const ref of refs) {
      const kf = storeKeyframes.get(ref.trackId)?.find((k) => k.id === ref.keyframeId);
      if (kf) items.push({ ...ref, originalTime: kf.time });
    }
    if (items.length === 0) return;
    dragStartX.current = e.clientX;
    setDragDeltaMs(0);
    setDraggingKeyframe({ items });
  }, [selectedKeyframes, selectionHas, setKeyframeSelection, storeKeyframes]);
  
  // ==========================================================================
  // DIALOG BAR DRAGGING
  // ==========================================================================
  
  const handleDialogDragStart = useCallback((e: React.MouseEvent, dialogId: string, currentStartTime: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingDialog({ dialogId });
    dragStartX.current = e.clientX;
    dragStartTime.current = currentStartTime;
  }, []);
  
  useEffect(() => {
    if (!draggingDialog) return;
    // The whole drag is one undo step (spec 018).
    const { beginHistoryGroup, endHistoryGroup } = useSceneStore.getState();
    beginHistoryGroup();
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX.current;
      const deltaTime = (deltaX / pixelsPerSecond) * 1000;
      const newTime = Math.max(0, dragStartTime.current + deltaTime);
      
      updateDialog(draggingDialog.dialogId, { startTime: Math.round(newTime) });
    };
    
    const handleMouseUp = () => {
      setDraggingDialog(null);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      endHistoryGroup();
    };
  }, [draggingDialog, pixelsPerSecond, updateDialog]);
  
  // ==========================================================================
  // SOUND BAR DRAGGING (spec 007)
  // ==========================================================================

  const handleSoundDragStart = useCallback((e: React.MouseEvent, soundId: string, mode: 'move' | 'end', barEnd: number) => {
    e.preventDefault();
    e.stopPropagation();
    const sound = useSceneStore.getState().scene?.sounds.find((s) => s.id === soundId);
    if (!sound) return;
    selectSingle(soundId, 'sound');
    dragStartX.current = e.clientX;
    setDraggingSound({ soundId, mode, startTime: sound.startTime ?? 0, endTime: sound.endTime, barEnd });
  }, [selectSingle]);

  useEffect(() => {
    if (!draggingSound) return;
    // The whole drag is one undo step (spec 018)
    const { beginHistoryGroup, endHistoryGroup } = useSceneStore.getState();
    beginHistoryGroup();
    const { soundId, mode, startTime, endTime, barEnd } = draggingSound;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaMs = Math.round(((e.clientX - dragStartX.current) / pixelsPerSecond) * 1000);
      if (mode === 'move') {
        const shift = Math.max(-startTime, deltaMs);
        setSoundTiming(soundId, {
          startTime: startTime + shift,
          ...(endTime !== undefined ? { endTime: endTime + shift } : {}),
        });
      } else {
        setSoundTiming(soundId, { endTime: Math.max(startTime + 100, barEnd + deltaMs) });
      }
    };
    const handleMouseUp = () => setDraggingSound(null);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      endHistoryGroup();
    };
  }, [draggingSound, pixelsPerSecond, setSoundTiming]);

  useEffect(() => {
    if (!draggingKeyframe) return;
    const times = draggingKeyframe.items.map((i) => i.originalTime);
    const minDelta = -Math.min(...times);
    const maxDelta = duration - Math.max(...times);
    let delta = 0;
    let moved = false;
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX.current;
      if (Math.abs(deltaX) >= DRAG_THRESHOLD_PX) moved = true;
      if (!moved) return;
      delta = Math.round(Math.min(maxDelta, Math.max(minDelta, (deltaX / pixelsPerSecond) * 1000)));
      setDragDeltaMs(delta);
    };
    
    const handleMouseUp = () => {
      if (moved) {
        suppressNextClick.current = true;
        if (delta !== 0) {
          // One drag is one undo step (spec 018); a keyframe landing on an
          // occupied slot replaces the occupant (spec 019).
          const { beginHistoryGroup, endHistoryGroup } = useSceneStore.getState();
          beginHistoryGroup();
          try {
            moveKeyframes(draggingKeyframe.items, delta, duration);
          } finally {
            endHistoryGroup();
          }
        }
      }
      setDragDeltaMs(0);
      setDraggingKeyframe(null);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingKeyframe, pixelsPerSecond, duration, moveKeyframes]);
  
  // ==========================================================================
  // MARQUEE RECT SELECTION
  // ==========================================================================
  
  const handleTracksAreaMouseDown = useCallback((e: React.MouseEvent) => {
    // Only start marquee on left-click directly on the tracks area (not on a keyframe)
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.keyframe}`) || target.closest(`.${styles.playheadLine}`)) return;
    
    e.preventDefault();
    const rect = tracksAreaRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMarquee({ startX: x, startY: y, currentX: x, currentY: y });
    
    // If not shift-clicking, clear existing selection
    if (!e.shiftKey) {
      clearKeyframeSelection();
    }
  }, [clearKeyframeSelection]);
  
  useEffect(() => {
    if (!marquee) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const rect = tracksAreaRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMarquee(prev => prev ? { ...prev, currentX: e.clientX - rect.left, currentY: e.clientY - rect.top } : null);
    };
    
    const handleMouseUp = () => {
      if (!marquee || !tracksAreaRef.current) {
        setMarquee(null);
        return;
      }
      
      // Calculate marquee bounds (in pixels relative to tracksArea)
      const left = Math.min(marquee.startX, marquee.currentX);
      const right = Math.max(marquee.startX, marquee.currentX);
      const top = Math.min(marquee.startY, marquee.currentY);
      const bottom = Math.max(marquee.startY, marquee.currentY);
      
      // Only select if marquee is at least 5px in either direction
      if (right - left >= MARQUEE_MIN_PX || bottom - top >= MARQUEE_MIN_PX) {
        // Find all keyframe diamonds within the marquee
        const areaRect = tracksAreaRef.current.getBoundingClientRect();
        const diamonds = tracksAreaRef.current.querySelectorAll(`[data-kf-id]`);
        const selected: Array<{ trackId: string; keyframeId: string }> = [];
        
        diamonds.forEach(diamond => {
          const dRect = diamond.getBoundingClientRect();
          const dCenterX = dRect.left + dRect.width / 2 - areaRect.left;
          const dCenterY = dRect.top + dRect.height / 2 - areaRect.top;
          
          if (dCenterX >= left && dCenterX <= right && dCenterY >= top && dCenterY <= bottom) {
            const kfIds = (diamond.getAttribute('data-kf-id') || '').split(',');
            const trackId = diamond.getAttribute('data-track-id') || '';
            for (const kfId of kfIds) {
              if (kfId && trackId) {
                selected.push({ trackId, keyframeId: kfId });
              }
            }
          }
        });
        
        if (selected.length > 0) {
          setKeyframeSelection(selected);
        }
      }
      
      setMarquee(null);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [marquee, setKeyframeSelection]);
  
  // Close context menu when clicking elsewhere
  useEffect(() => {
    if (!contextMenu) return;
    
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [contextMenu]);

  if (!scene) {
    return (
      <div className={styles.emptyTimeline}>
        <p>No scene loaded</p>
      </div>
    );
  }

  const totalWidth = (duration / 1000) * pixelsPerSecond;

  return (
    <div className={styles.timelinePanel}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.playbackControls}>
          <button
            className={styles.controlButton}
            onClick={stop}
            title="Stop"
          >
            ⏹
          </button>
          <button
            className={styles.controlButton}
            onClick={playFromStart}
            title="From start (Shift+Space)"
            aria-label="Preview from start"
            data-testid="preview-from-start"
          >
            <PlayFromStartIcon size={14} />
          </button>
          <button
            className={styles.controlButton}
            onClick={isPlaying ? pause : play}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
        </div>

        <div className={styles.timeDisplay}>
          <span className={styles.currentTime} data-testid="timeline-current-time">{(playheadPosition / 1000).toFixed(2)}s</span>
          <span className={styles.separator}>/</span>
          <span className={styles.totalTime}>{(duration / 1000).toFixed(1)}s</span>
        </div>

        <div className={styles.zoomControls}>
          <button
            className={styles.zoomButton}
            onClick={() => zoomTimeline('out')}
            disabled={timelineZoom <= MIN_ZOOM}
            title="Zoom Out"
          >
            −
          </button>
          <span className={styles.zoomLevel} data-testid="timeline-zoom-level">{Math.round(timelineZoom * 100)}%</span>
          <button
            className={styles.zoomButton}
            onClick={() => zoomTimeline('in')}
            disabled={timelineZoom >= MAX_ZOOM}
            title="Zoom In"
          >
            +
          </button>
        </div>
        

      </div>

      {/* Timeline content: while ▶ Preview plays the labels are out of reach and
          the ruler and tracks only jump the preview (spec 009) */}
      <div className={styles.timelineContent} data-previewing={isPlaying ? '' : undefined}>
        {/* Labels column */}
        <div 
          ref={labelsColumnRef}
          className={styles.labelsColumn}
          onScroll={handleLabelsScroll}
          {...inertWhen(isPlaying)}
        >
          {/* Time ruler header */}
          <div className={styles.rulerHeader}>
            <span>Time</span>
          </div>
          
          {/* Lane groups */}
          {laneGroups.map((group) => (
            <div key={group.id} className={styles.laneGroup}>
              <LaneGroupHeader 
                group={group} 
                onToggle={() => toggleGroup(group.id)}
                onAddTrack={
                  group.id === 'items' ? handleAddItem :
                  group.id === 'sounds' ? handleAddSound :
                  undefined
                }
              />
              
              {!group.collapsed && group.tracks.map((track) => {
                const isBackground = track.type === 'background';
                // The Background row is always open on its one row (spec 040 D-3)
                const isExpanded = isBackground || expandedTracks.has(track.id);
                const isSelected = !isBackground && selection.ids.includes(track.id);
                const properties = propertiesOf(track);

                return (
                  <React.Fragment key={track.id}>
                    <div
                      className={`${styles.trackLabelRow} ${isSelected ? styles.selected : ''}`}
                      data-testid={isBackground ? 'background-track-label' : undefined}
                      // Not a sprite: a click selects the scene, where its states are edited
                      onClick={() => (isBackground ? selectSingle('scene', 'scene') : selectSingle(track.id, track.type as any))}
                    >
                      {track.expandable && (
                        <button 
                          className={`${styles.expandButton} ${isExpanded ? styles.expanded : ''}`}
                          onClick={(e) => { e.stopPropagation(); toggleExpand(track.id); }}
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${track.name}`}
                          aria-expanded={isExpanded}
                        >
                          ▶
                        </button>
                      )}
                      {!track.expandable && <span className={styles.expandPlaceholder} />}
                      <span className={styles.trackIcon}>{track.icon}</span>
                      <span className={styles.trackName}>{track.name}</span>
                    </div>
                    
                    {/* Property sub-rows */}
                    {isExpanded && properties.map((prop) => (
                      <div 
                        key={prop.key} 
                        className={styles.propertyLabelRow}
                        title="Double-click to add keyframe at playhead"
                        data-testid="property-row-label"
                        data-track-id={track.id}
                        data-property={prop.key}
                      >
                        <span className={styles.propertyName}>{prop.label}</span>
                        <button 
                          className={styles.addKeyframeButton}
                          onClick={(e) => { e.stopPropagation(); addKeyframe(track.id, prop.key); }}
                          title="Add keyframe at playhead"
                          aria-label={`Add ${isBackground ? 'Background ' : ''}${prop.label} keyframe at playhead`}
                          data-testid="add-keyframe"
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
              
              {!group.collapsed && group.tracks.length === 0 && (
                <div className={styles.emptyGroup}>
                  No {group.name.toLowerCase()}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Timeline tracks column */}
        <div 
          ref={timelineRef}
          className={styles.tracksColumn}
          onScroll={handleTracksScroll}
          onMouseDownCapture={isPlaying ? handlePreviewJumpDown : undefined}
          onClickCapture={isPlaying ? swallowWhilePreviewing : undefined}
          onDoubleClickCapture={isPlaying ? swallowWhilePreviewing : undefined}
          onContextMenuCapture={isPlaying ? swallowWhilePreviewing : undefined}
          data-testid="timeline-tracks-column"
        >
          {/* Time ruler */}
          <div ref={rulerAreaRef} className={styles.rulerArea} onMouseDown={handleRulerMouseDown} style={{ width: totalWidth }} data-testid="timeline-ruler">
            <TimeRuler 
              duration={duration} 
              zoom={timelineZoom} 
              scrollLeft={timelineScrollLeft}
              width={timelineWidth}
            />
            {/* Playhead head - stays in ruler area */}
            <div 
              className={styles.playheadHead}
              style={{ left: playheadX, cursor: isDraggingPlayhead ? 'grabbing' : 'grab' }}
              onMouseDown={handlePlayheadMouseDown}
            />
          </div>
          
          {/* Track rows */}
          <div 
            ref={tracksAreaRef}
            className={styles.tracksArea} 
            style={{ width: totalWidth }}
            onClick={handleTimelineClick}
            onMouseDown={handleTracksAreaMouseDown}
          >
            {laneGroups.map((group) => (
              <div key={group.id} className={styles.laneGroupTracks}>
                {/* Group header spacer */}
                <div className={styles.groupHeaderSpacer} />
                
                {!group.collapsed && group.tracks.map((track) => {
                  const isBackground = track.type === 'background';
                  const isExpanded = isBackground || expandedTracks.has(track.id);
                  const isSelected = !isBackground && selection.ids.includes(track.id);
                  const properties = propertiesOf(track);
                                    
                  const startX = (track.startTime / 1000) * pixelsPerSecond;
                  const endX = ((track.endTime || duration) / 1000) * pixelsPerSecond;
                  
                  return (
                    <React.Fragment key={track.id}>
                      <div className={`${styles.trackTimelineRow} ${isSelected ? styles.selected : ''}`}>
                        {/* Item bar */}
                        <div 
                          className={`${styles.itemBar} ${styles[track.type]} ${track.type === 'dialog' || track.type === 'sound' ? styles.draggable : ''} ${draggingDialog?.dialogId === track.id || draggingSound?.soundId === track.id ? styles.dragging : ''}`}
                          style={{
                            left: startX,
                            width: Math.max(20, endX - startX),
                            cursor: track.type === 'dialog' || track.type === 'sound' ? 'grab' : 'pointer',
                          }}
                          data-testid={`timeline-bar-${track.type}`}
                          data-track-id={track.id}
                          data-start-ms={track.startTime}
                          data-end-ms={track.endTime ?? duration}
                          onClick={() => (isBackground ? selectSingle('scene', 'scene') : selectSingle(track.id, track.type as any))}
                          onMouseDown={
                            track.type === 'dialog' ? (e) => handleDialogDragStart(e, track.id, track.startTime)
                              : track.type === 'sound' ? (e) => handleSoundDragStart(e, track.id, 'move', track.endTime ?? duration)
                                : undefined
                          }
                        >
                          {/* Inset from the bar start; a long name truncates at its end and shows whole in the tooltip (F-027) */}
                          <span className={styles.itemBarLabel} data-testid="timeline-item-label" title={track.name}>{track.name}</span>
                          {track.note && <span className={styles.itemBarLabel} data-testid="track-note"> · {track.note}</span>}
                          {track.type === 'sound' && (
                            <div
                              className={styles.itemBarEndHandle}
                              data-testid="sound-bar-end-handle"
                              title="Drag to set the end"
                              onMouseDown={(e) => handleSoundDragStart(e, track.id, 'end', track.endTime ?? duration)}
                            />
                          )}
                        </div>
                        
                      </div>
                      
                      {/* Property sub-rows */}
                      {isExpanded && properties.map((prop) => {
                        const propKeyframes = getTrackKeyframes(track.id, prop.key);
                        const grouped = groupKeyframesByTime(propKeyframes);
                        const isCompound = prop.key in COMPOUND_PROPERTIES;
                        return (
                          <div 
                            key={prop.key} 
                            className={styles.propertyTimelineRow}
                            onDoubleClick={(e) => handlePropertyDoubleClick(e, track.id, prop.key)}
                            onContextMenu={(e) => handleRowContextMenu(e, track.id, prop.key)}
                            title="Double-click to add keyframe • Right-click to paste"
                            data-testid="property-row"
                            data-track-id={track.id}
                            data-property={prop.key}
                          >
                            {grouped.map((group) => {
                              const isSelectedKf = group.keyframes.some(kf => selectedKeyframes.some(sk => sk.keyframeId === kf.id));
                              const isDragging = group.keyframes.some(kf => draggingKeyframe?.items.some((i) => i.keyframeId === kf.id));
                              const shownTime = isDragging ? group.time + dragDeltaMs : group.time;
                              const kfX = (shownTime / 1000) * pixelsPerSecond;
                              const kfIds = group.keyframes.map(kf => kf.id);
                              const tag = group.keyframes.find((kf) => kf.orientation === 'portrait' || kf.orientation === 'landscape')?.orientation;
                              const change = isBackground ? group.keyframes[0]?.transition : undefined;
                              const titleText = isBackground
                                // Spec 040 D-4: the change's transition type is the diamond's tooltip
                                ? `${change && change.type !== 'none' ? change.type : 'cut'}\nBackground → ${group.keyframes[0]?.value} @ ${(group.time / 1000).toFixed(2)}s (${backgroundTransitionText(change)})\nClick to edit • Drag to move • Right-click for more`
                                : isCompound
                                ? `${prop.key} (${group.keyframes.map(kf => `${SUB_PROPERTY_LABELS[kf.property] || kf.property}: ${kf.value}`).join(', ')}) @ ${(group.time / 1000).toFixed(2)}s\nClick to edit • Drag to move • Right-click for more`
                                : `${prop.key} = ${group.keyframes[0]?.value} @ ${(group.time / 1000).toFixed(2)}s\nClick to edit • Drag to move • Right-click for more`;
                              const barWidth = change && change.type !== 'none' && change.duration > 0 ? (change.duration / 1000) * pixelsPerSecond : 0;
                              return (
                                <React.Fragment key={`group-${group.time}`}>
                                {barWidth > 0 && (
                                  <div
                                    className={styles.transitionBar}
                                    style={{ left: kfX, width: barWidth }}
                                    data-testid="background-transition-bar"
                                    data-time={group.time}
                                    data-duration={change!.duration}
                                    aria-hidden="true"
                                  />
                                )}
                                <div
                                  className={`${styles.keyframe} ${styles.propertyKeyframe} ${tag ? styles.taggedKeyframe : ''} ${isSelectedKf ? styles.selectedKeyframe : ''} ${isDragging && dragDeltaMs !== 0 ? styles.draggingKeyframe : ''}`}
                                  style={{ left: kfX, cursor: 'ew-resize' }}
                                  data-kf-id={kfIds.join(',')}
                                  data-track-id={track.id}
                                  data-property={prop.key}
                                  data-time={group.time}
                                  data-orientation={tag}
                                  data-transition={isBackground ? (change && change.type !== 'none' ? change.type : 'cut') : undefined}
                                  data-selected={isSelectedKf ? 'true' : undefined}
                                  onClick={(e) => handleKeyframeClick(e, track.id, group.keyframes, prop.key)}
                                  onDoubleClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => handleKeyframeDragStart(e, track.id, kfIds)}
                                  onContextMenu={(e) => handleKeyframeContextMenu(e, track.id, prop.key, kfIds)}
                                  title={tag ? `${titleText}\n${tag === 'portrait' ? 'Portrait' : 'Landscape'} only` : titleText}
                                />
                                </React.Fragment>
                              );
                            })}
                            {prop.key === 'state' && !isBackground && (derivedStateKeys.get(track.id) ?? []).map((kf) => (
                              // Read-only: retime the line to move it (spec 019 D-3)
                              <div
                                key={kf.id}
                                className={`${styles.derivedKeyframe} ${kf.orientation ? styles.taggedKeyframe : ''}`}
                                style={{ left: (kf.time / 1000) * pixelsPerSecond }}
                                data-testid="derived-keyframe"
                                data-derived="expression"
                                data-track-id={track.id}
                                data-property="state"
                                data-time={kf.time}
                                data-orientation={kf.orientation}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onDoubleClick={(e) => e.stopPropagation()}
                                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                title={`state = ${kf.value} @ ${(kf.time / 1000).toFixed(2)}s\nFrom a dialog line's expression • Retime the line to move it`}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                
                {!group.collapsed && group.tracks.length === 0 && (
                  <div className={styles.emptyGroupSpacer} />
                )}
              </div>
            ))}
            
            {/* Marquee selection rect */}
            {marquee && (
              <div
                className={styles.marqueeRect}
                style={{
                  left: Math.min(marquee.startX, marquee.currentX),
                  top: Math.min(marquee.startY, marquee.currentY),
                  width: Math.abs(marquee.currentX - marquee.startX),
                  height: Math.abs(marquee.currentY - marquee.startY),
                }}
              />
            )}
            
            {/* Playhead line - extends through all tracks */}
            <div 
              className={styles.playheadLine}
              style={{ left: playheadX }}
            />
          </div>
        </div>
      </div>
      
      {/* Context menu: Delete / Copy on a diamond, Paste here on a row */}
      {contextMenu && (() => {
        const rowProps = TIMELINE_ROW_PROPERTIES[contextMenu.property] ?? [contextMenu.property];
        const canPaste = Boolean(keyframeClipboard?.some((c) => rowProps.includes(c.property)));
        return (
        <div 
          className={styles.contextMenu}
          style={{ left: contextMenu.x, top: contextMenu.y }}
          role="menu"
          data-testid="timeline-context-menu"
        >
          {contextMenu.targets.length > 0 && (
            <>
              <button 
                className={styles.contextMenuItem}
                role="menuitem"
                onClick={() => { deleteKeyframes(contextMenu.targets); setContextMenu(null); }}
              >
                🗑️ Delete
              </button>
              <button 
                className={styles.contextMenuItem}
                role="menuitem"
                onClick={() => { copyKeyframes(contextMenu.targets); setContextMenu(null); }}
              >
                📋 Copy
              </button>
            </>
          )}
          <button 
            className={styles.contextMenuItem}
            role="menuitem"
            disabled={!canPaste}
            onClick={() => { pasteKeyframes(contextMenu.trackId, contextMenu.property); setContextMenu(null); }}
          >
            📌 Paste here
          </button>
        </div>
        );
      })()}
      
      {/* Keyframe Editor Popover */}
      {keyframeEditor && (() => {
        const { trackId, keyframes: editorKfs, property } = keyframeEditor;
        const primaryKf = editorKfs[0];
        const isCompound = property in COMPOUND_PROPERTIES;
        const allKfIds = editorKfs.map(kf => kf.id);
        const isBackgroundKey = trackId === BACKGROUND_TRACK_ID;
                
        return (
        <div 
          className={styles.keyframeEditor}
          style={{ 
            left: keyframeEditor.x, 
            top: keyframeEditor.y,
            transform: 'translate(-50%, -100%)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.keyframeEditorHeader}>
            <span>Edit Keyframe</span>
            <button 
              className={styles.keyframeEditorClose}
              onClick={() => setKeyframeEditor(null)}
            >
              ×
            </button>
          </div>
          
          <div className={styles.keyframeEditorBody}>
            <div className={styles.keyframeEditorRow}>
              <label>Property:</label>
              <span className={styles.keyframeEditorPropertyName}>
                {property}
              </span>
            </div>
            
            <div className={styles.keyframeEditorRow}>
              <label>Time:</label>
              <span>{(primaryKf.time / 1000).toFixed(2)}s</span>
            </div>
            
            {!isBackgroundKey && (
            <div className={styles.keyframeEditorRow}>
              <label>Orientation:</label>
              <span className={styles.keyframeEditorPropertyName}>
                {primaryKf.orientation ? (primaryKf.orientation === 'portrait' ? '📱 Portrait' : '📱 Landscape') : '🖥️ All (Desktop)'}
              </span>
            </div>
            )}
                        
            {/* Value fields — compound properties show multiple rows */}
            {isBackgroundKey ? (
              // Spec 040 D-4: the background's states and the change's transition
              <>
                <div className={styles.keyframeEditorRow}>
                  <label>State:</label>
                  <select
                    className={styles.keyframeEditorSelect}
                    aria-label="Background state"
                    data-testid="background-key-state"
                    value={primaryKf.value as string}
                    onChange={(e) => handleKeyframeValueChange(trackId, primaryKf.id, e.target.value, true)}
                  >
                    {backgroundStates(scene?.background).map((st) => (
                      <option key={st.name} value={st.name}>{st.name}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.keyframeEditorTransition} data-testid="background-key-transition">
                  <TransitionFields
                    value={primaryKf.transition ?? CUT}
                    onChange={(updates) => handleTransitionChange(trackId, primaryKf, updates)}
                    fieldClassName={styles.keyframeEditorRow}
                  />
                </div>
              </>
            ) : isCompound ? (
              editorKfs.map(kf => {
                const isHinge = kf.property.startsWith('hinge.');
                return (
                  <div key={kf.id} className={styles.keyframeEditorRow}>
                    <label>{SUB_PROPERTY_LABELS[kf.property] || kf.property}:</label>
                    <input
                      type="number"
                      className={styles.keyframeEditorInput}
                      value={kf.value}
                      step={0.1}
                      min={isHinge ? 0 : undefined}
                      max={isHinge ? 100 : undefined}
                      onChange={(e) => handleKeyframeValueChange(trackId, kf.id, parseFloat(e.target.value))}
                    />
                  </div>
                );
              })
            ) : property === 'state' ? (
              <div className={styles.keyframeEditorRow}>
                <label>Value:</label>
                {(() => {
                  const sprite = scene?.sprites.find(s => s.id === trackId);
                  const availableStates = sprite?.states || [];
                  return (
                    <select
                      className={styles.keyframeEditorSelect}
                      value={primaryKf.value as string}
                      onChange={(e) => handleKeyframeValueChange(trackId, primaryKf.id, e.target.value, true)}
                    >
                      {availableStates.length === 0 && (
                        <option value="">No states defined</option>
                      )}
                      {availableStates.map(state => (
                        <option key={state.name} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            ) : isFlipProperty(property) ? (
              <div className={styles.keyframeEditorRow}>
                <label>Value:</label>
                <select
                  className={styles.keyframeEditorSelect}
                  aria-label="Flip value"
                  value={primaryKf.value !== 0 && primaryKf.value !== '0' ? 1 : 0}
                  onChange={(e) => handleKeyframeValueChange(trackId, primaryKf.id, Number(e.target.value))}
                >
                  <option value={1}>Flipped</option>
                  <option value={0}>As drawn</option>
                </select>
              </div>
            ) : property === 'opacity' ? (
              <div className={styles.keyframeEditorRow}>
                <label>Value:</label>
                <div className={styles.keyframeEditorValueGroup}>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={primaryKf.value}
                    onChange={(e) => handleKeyframeValueChange(trackId, primaryKf.id, parseFloat(e.target.value))}
                    className={styles.keyframeEditorSlider}
                  />
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    className={styles.keyframeEditorInput}
                    value={primaryKf.value}
                    onChange={(e) => handleKeyframeValueChange(trackId, primaryKf.id, parseFloat(e.target.value))}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.keyframeEditorRow}>
                <label>Value:</label>
                <input
                  type="number"
                  className={styles.keyframeEditorInput}
                  value={primaryKf.value}
                  step={primaryKf.property.includes('rotation') ? 1 : 0.1}
                  onChange={(e) => handleKeyframeValueChange(trackId, primaryKf.id, parseFloat(e.target.value))}
                />
              </div>
            )}
            
            {/* A flip steps (spec 038 D-3), a background change has its transition: no easing to offer. */}
            {!isFlipProperty(property) && !isBackgroundKey && (
            <div className={styles.keyframeEditorRow}>
              <label>Easing:</label>
              <select
                className={styles.keyframeEditorSelect}
                value={primaryKf.easing || 'linear'}
                onChange={(e) => handleEasingChange(
                  trackId,
                  allKfIds,
                  e.target.value as Keyframe['easing']
                )}
              >
                <option value="linear">Linear</option>
                <option value="easeIn">Ease In</option>
                <option value="easeOut">Ease Out</option>
                <option value="easeInOut">Ease In-Out</option>
              </select>
            </div>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default TimelinePanel;
